import React from "react"
import { OverlayTrigger, Tooltip } from "react-bootstrap"
import { FormattedMessage, injectIntl } from "react-intl"
import NewBankAccountOnFileModal from "../../common/bank-account/NewBankAccountOnFileModal"
import NewCreditCardModal from "../../common/credit-card/newcreditcardmodal.component.jsx"
import Icon from "../../common/icon/icon.component"
import { GlobalHelper } from "../../indexImports"
import { integrationKeyRainforest } from "../../integration/IntegrationConstants"
import BankAccountLabel from "../../payment/bankaccount.component"
import CreditCardLabel from "../../payment/creditcard.component"
import { BankAccountDto } from "../../state/bank-account/BankAccount"
import { CardDto } from "../../state/card/Card"
import IntlProps from "../../state/helpers/IntlProps"
import { PAYMENT_TYPE } from "../../state/helpers/Payment"
import { INTEGRATION_KEY } from "../../state/integration/Constants"

type BaseProps = {
    showModal: boolean
    disabled: boolean
    contactId: string
    onOpenModal: () => void
    onCloseModal: () => void
    onSubmitModal: () => void
    locationId: string
}

type PaymentMethodLabelProps = {
    show: boolean
    paymentMethod: PAYMENT_TYPE
    creditCard?: CardDto
    bankAccount?: BankAccountDto
} & BaseProps

const PaymentMethodLabel = (props: PaymentMethodLabelProps & IntlProps) => {
    const getLabelForCaptureMethod = () => {
        switch (props.paymentMethod) {
        case PAYMENT_TYPE.BANK_TRANSFER:
            return (
                <BankAccountPaymentMethodLabel
                    showModal={props.showModal}
                    disabled={props.disabled}
                    bankAccount={props.bankAccount}
                    contactId={props.contactId}
                    onOpenModal={props.onOpenModal}
                    onCloseModal={props.onCloseModal}
                    onSubmitModal={props.onSubmitModal}
                    locationId={props.locationId}
                />
            )
        case PAYMENT_TYPE.CREDIT_CARD:
        default:
            return (
                <CreditCardPaymentMethodLabel
                    showModal={props.showModal}
                    disabled={props.disabled}
                    creditCard={props.creditCard}
                    contactId={props.contactId}
                    onOpenModal={props.onOpenModal}
                    onCloseModal={props.onCloseModal}
                    onSubmitModal={props.onSubmitModal}
                    locationId={props.locationId}
                />
            )
        }
    }

    return props.show && getLabelForCaptureMethod()
}

type CreditCardPaymentMethodLabelProps = {
    creditCard?: CardDto
} & BaseProps

const CreditCardPaymentMethodLabel = injectIntl((props: CreditCardPaymentMethodLabelProps & IntlProps) => {
    const integration = GlobalHelper.getPaymentIntegrationPerLocation(props.locationId)
    return (
        <>
            {props.creditCard ? (
                <p className="mb-0 text-secondary">
                    <small>
                        <span className="mr-2">
                            <FormattedMessage
                                id="invoice.cards_on_file.will_charge"
                                defaultMessage="Will charge"
                            />
                        </span>
                        <CreditCardLabel value={props.creditCard} />
                    </small>
                </p>
            ) : (
                <OverlayTrigger
                    placement="bottom"
                    overlay={
                        !integration ? (
                            <Tooltip id={"credit-card-button"}>
                                <FormattedMessage
                                    id="invoice.payment.method_no_integration_for_location"
                                    defaultMessage="This document is assigned to a location that is not linked to to an existing payment integration."
                                />
                            </Tooltip>) : (<div />)}>
                    <button
                        className="btn btn-sm btn-outline-danger"
                        disabled={!integration}
                        onClick={props.onOpenModal}
                    >
                        <small>
                            <Icon value="exclamation" className="mr-1" />
                            <FormattedMessage
                                id="invoice.cards_on_file.no_card"
                                defaultMessage="No card on file"
                            />
                        </small>
                    </button>
                </OverlayTrigger>
            )}
            {props.showModal && integration && (
                <NewCreditCardModal
                    contactId={props.contactId}
                    saveAndChargeLater={true}
                    onClose={props.onCloseModal}
                    onSubmit={props.onSubmitModal}
                    locationId={props.locationId}
                />
            )}
        </>
    )
})

type BankAccountPaymentMethodLabelProps = {
    bankAccount?: BankAccountDto
} & BaseProps

const BankAccountPaymentMethodLabel = injectIntl((props: BankAccountPaymentMethodLabelProps & IntlProps) => {
    const integration = GlobalHelper.getPaymentIntegrationPerLocation(props.locationId)

    return (
        <>
            {props.bankAccount ? (
                <p className="mb-0 text-secondary">
                    <small>
                        <span className="mr-2">
                            <FormattedMessage
                                id="invoice.cards_on_file.will_charge"
                                defaultMessage="Will charge"
                            />
                        </span>
                        <BankAccountLabel value={props.bankAccount} />
                    </small>
                </p>
            ) : (
                <OverlayTrigger
                    placement="bottom"
                    overlay={
                        !integration || integration.attributes.integrationKey !== integrationKeyRainforest ? (
                            <Tooltip id={"credit-card-button"}>
                                <FormattedMessage
                                    id="invoice.payment.method_no_integration_for_location"
                                    defaultMessage="This document is assigned to a location that is not linked to to an existing payment integration."
                                />
                            </Tooltip>) : (<div />)}>
                    <button
                        className="btn btn-sm btn-outline-danger"
                        disabled={!integration || integration.attributes.integrationKey !== integrationKeyRainforest}
                        onClick={props.onOpenModal}
                    >
                        <small>
                            <Icon value="exclamation" className="mr-1" />
                            <FormattedMessage
                                id="invoice.cards_on_file.no_bank_account"
                                defaultMessage="No bank account on file"
                            />
                        </small>
                    </button>
                </OverlayTrigger>
            )}
            {props.showModal && (integration && integration.attributes.integrationKey === integrationKeyRainforest) && (
                <NewBankAccountOnFileModal
                    contactId={props.contactId}
                    integrationKey={INTEGRATION_KEY.RAINFOREST}
                    onClose={props.onCloseModal}
                    onSubmit={props.onSubmitModal}
                    locationId={props.locationId}
                />
            )}
        </>
    )
})

export default injectIntl(PaymentMethodLabel)


// WEBPACK FOOTER //
// ./src/invoice/recurring/PaymentMethodLabel.tsx